/**
 * Unpaid-invoice lockout, seen from the browser.
 *
 * When an organisation is suspended for an unpaid Hospora invoice, the gateway (and the API itself) answers every
 * request with HTTP 402 `{ error, code: 'org_suspended' }`. Both apps catch that in ONE place, here, instead of in
 * each of the dozens of `fetch` calls: `watchOrgSuspension` wraps `window.fetch` once at start-up and records the
 * first such answer in `suspension`, which each app's root component turns into a full-screen, non-dismissable page.
 *
 * The wrapper never changes what a caller sees: it reads a clone of the response and hands back the original.
 * Only requests to the app's own API are looked at (never Square, Sentry, fonts…).
 */
import { ref } from 'vue'

export const ORG_SUSPENDED_CODE = 'org_suspended'
export const HOSPORA_BILLING_URL = 'https://hospora.app/console/billing'

export interface OrgSuspension {
  /** The server's explanation (for staff); guests are shown a neutral line instead. */
  message: string
}

/** Non-null once the API has answered 402 `org_suspended`. Reactive. */
export const suspension = ref<OrgSuspension | null>(null)

/** True for a 402 response body that is the org-suspended answer. */
export function isOrgSuspendedBody(body: unknown): body is { code: string; error?: unknown } {
  return !!body && typeof body === 'object' && (body as { code?: unknown }).code === ORG_SUSPENDED_CODE
}

export function markSuspended(body: { error?: unknown } | null | undefined): void {
  const message = typeof body?.error === 'string' && body.error.trim() ? body.error.trim() : ''
  suspension.value = { message }
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  return (input as Request).url ?? ''
}

const WRAPPED = Symbol.for('hospora.bookings.suspensionWatcher')

/**
 * Wrap `window.fetch` so a 402 `org_suspended` from `apiBase` sets `suspension`. Safe to call more than once.
 * Returns a function that restores the original `fetch` (used by tests).
 */
export function watchOrgSuspension(apiBase: string, target: { fetch: typeof fetch } = globalThis as unknown as { fetch: typeof fetch }): () => void {
  const current = target.fetch as typeof fetch & { [WRAPPED]?: typeof fetch }
  if (current[WRAPPED]) return () => undefined
  const original = target.fetch
  const base = apiBase.replace(/\/+$/, '')
  const wrapped = (async (input: RequestInfo | URL, init?: RequestInit) => {
    const response = await original.call(target, input, init)
    if (response.status === 402 && requestUrl(input).startsWith(base)) {
      try {
        const body = await response.clone().json()
        if (isOrgSuspendedBody(body)) markSuspended(body)
      } catch { /* not JSON: an ordinary 402, not ours */ }
    }
    return response
  }) as typeof fetch & { [WRAPPED]?: typeof fetch }
  wrapped[WRAPPED] = original
  target.fetch = wrapped
  return () => { if (target.fetch === wrapped) target.fetch = original }
}
